// Web's reservation-tracker rows: one shape for the trip tracker
// (routes/Reservations.tsx), the cross-trip agenda (GlobalReservations.tsx),
// the homepage teaser (ReservationTeaser via all-reservations.ts) and
// Overview's "Before you go". Every rule comes from @core/reservation-core
// (window, open moment, row state, secured-by-booking); this file only
// joins a trip's bookings and stays, sorts, counts, and words the lines.
//
// The pure trip-state updaters for the tracker's writes are here too
// (track, set a date, mark booked, dismiss). Callers run them through
// trip-state.ts updateTripState, which does the fetch / If-Match / retry.

import {
  classifyReservation,
  openMomentText,
  reservationOpenMoment,
  reservationWindow,
  type Countdown,
  type OpenMoment,
  type ReservationWindow,
} from '@core/reservation-core';
import { timezoneForDate } from '@core/day-plan-core';
import { isActiveBooking } from '@core/plan-core';
import { appendReservation, genId, mergeDefined } from './trip-state-core';
import type { Booking, Reservation, TripState, TripStay } from './types';

export type TrackerState = 'book' | 'queued' | 'conf';

export interface TrackerRow<R extends Reservation = Reservation> {
  res: R;
  /** Matches the entry for writes: its id, else refKind + refId. */
  key: ReservationKey;
  state: TrackerState;
  countdown: Countdown;
  window: ReservationWindow;
  /** When the window opens, with the release clock time when known. */
  moment: OpenMoment | null;
  /** The day it's booked for: the matching booking's date, else targetDate. */
  bookedOn: string | null;
  /** Still to book, with a pending booking for the place (asked for, not confirmed). */
  requested: boolean;
}

export interface ReservationKey {
  id?: string | null;
  refKind: string;
  refId: string;
}

export interface TrackerContext {
  bookings?: Booking[] | null;
  stays?: TripStay[] | null;
  /** meta.timezone, the trip-wide fallback zone. */
  timezone?: string | null;
  today?: string;
}

/** The browser's zone, for "(8:00 PM your time)". */
export function viewerZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export function keyOf(r: Reservation): ReservationKey {
  const id = typeof r.id === 'string' && r.id ? r.id : null;
  return { id, refKind: r.refKind, refId: r.refId };
}

export function keyString(k: ReservationKey): string {
  return k.id || `${k.refKind}/${k.refId}`;
}

/** Build one row, or null for a dismissed / cancelled entry. */
export function trackerRow<R extends Reservation>(res: R, ctx: TrackerContext): TrackerRow<R> | null {
  const bookings = (ctx.bookings ?? []) as Booking[];
  const target = typeof res.targetDate === 'string' ? res.targetDate : null;
  // The venue's clock for an opensAt with a time and no zone: the stay the
  // table falls in.
  const zone = target ? timezoneForDate({ stays: ctx.stays ?? [], timezone: ctx.timezone ?? null }, target) : null;
  const opts = { today: ctx.today, zone, bookings };
  const cls = classifyReservation(res, opts);
  if (!cls) return null;
  const booking = bookings.find((b) => b && b.refId === res.refId && isActiveBooking(b));
  return {
    res,
    key: keyOf(res),
    state: cls.state as TrackerState,
    countdown: cls.countdown,
    window: reservationWindow(res, { zone }),
    moment: reservationOpenMoment(res, { zone }),
    bookedOn: (booking?.dateStart as string | undefined) || target,
    requested: cls.requested,
  };
}

export function trackerRows<R extends Reservation>(list: readonly R[], ctx: TrackerContext): TrackerRow<R>[] {
  const out: TrackerRow<R>[] = [];
  for (const r of list) {
    if (!r || typeof r !== 'object') continue;
    const row = trackerRow(r, ctx);
    if (row) out.push(row);
  }
  return out;
}

/**
 * Soonest opening first. Rows with no window (needs a date, a rule the
 * core can't read) go last; booked rows sort by the booked day.
 */
export function sortByOpening<T extends TrackerRow>(rows: readonly T[]): T[] {
  const openKey = (r: T) =>
    r.state === 'conf' ? `2|${r.bookedOn ?? '9999'}` : r.window.opensOn ? `0|${r.window.opensOn}` : '1|';
  return rows.slice().sort((a, b) => openKey(a).localeCompare(openKey(b)));
}

/** The stat tiles, named the same on every tracker surface. */
export const TRACKER_STATS = [
  { key: 'bookNow', label: 'Book now', sub: 'window open' },
  { key: 'thisWeek', label: 'Opens in 7 days', sub: 'set an alarm' },
  { key: 'later', label: 'Later', sub: 'further out or needs a date' },
  { key: 'booked', label: 'Booked', sub: 'secured' },
] as const;

export type TrackerStatKey = (typeof TRACKER_STATS)[number]['key'];

export function trackerStats(rows: readonly TrackerRow[]): Record<TrackerStatKey, number> & { total: number } {
  const s = { bookNow: 0, thisWeek: 0, later: 0, booked: 0, total: rows.length };
  for (const r of rows) {
    if (r.state === 'conf') s.booked++;
    else if (r.state === 'book') s.bookNow++;
    else if (r.countdown.daysFromToday != null && r.countdown.daysFromToday <= 7) s.thisWeek++;
    else s.later++;
  }
  return s;
}

/**
 * The window line under a row: "Opens Thu Oct 1, 12:00 JST (Wed Sep 30,
 * 8:00 PM your time)", "Opened …" once past, "Needs a date", or why there
 * is no window.
 */
export function windowLine(row: TrackerRow, viewer: string | null = viewerZone()): string {
  if (row.state === 'conf') return row.bookedOn ? `Booked for ${dayText(row.bookedOn)}` : 'Booked';
  const cd = row.countdown;
  if (cd.reason === 'needs-date') return 'Needs a date';
  if (cd.reason === 'rule-unparsed') return `Release rule not computed: ${row.window.rule ?? ''}`.trim();
  if (!row.moment) return cd.label;
  const { venue, viewer: mine } = openMomentText(row.moment, viewer);
  const past = cd.daysFromToday != null && cd.daysFromToday < 0;
  return `${past ? 'Opened' : 'Opens'} ${venue}${mine ? ` (${mine} your time)` : ''}`;
}

/** A days-ahead estimate standing in for a release rule the core can't read. */
export function estimateNote(row: TrackerRow): string | null {
  if (row.state === 'conf' || !row.window.ruleUnparsed || !row.window.opensOn) return null;
  return `Estimate. Release rule: ${row.window.rule}`;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function dayText(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${DOW[d.getUTCDay()]} ${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

// ─── catalog type ↔ tracker refKind ──────────────────────────────────
// The tracker stores the singular kind (MCP catalogTypeToRefKind, mobile
// ReservationSeed); the manifest and routes use the plural type.
const KIND_OF_TYPE: Record<string, string> = {
  hotels: 'hotel',
  restaurants: 'restaurant',
  bars: 'bar',
  attractions: 'attraction',
  experiences: 'experience',
  shopping: 'shopping',
  destinations: 'destination',
};
const TYPE_OF_KIND: Record<string, string> = Object.fromEntries(
  Object.entries(KIND_OF_TYPE).map(([t, k]) => [k, t]),
);

export function refKindOfType(type: string): string {
  return KIND_OF_TYPE[type] ?? type;
}

export function catalogTypeOfKind(kind: string | null | undefined): string {
  if (!kind) return 'restaurants';
  return TYPE_OF_KIND[kind] ?? kind;
}

// ─── pure updaters (run through updateTripState) ─────────────────────

function sameKind(a: string | null | undefined, b: string | null | undefined): boolean {
  return catalogTypeOfKind(a) === catalogTypeOfKind(b);
}

function matches(r: Reservation, k: ReservationKey): boolean {
  if (k.id) return r.id === k.id;
  return r.refId === k.refId && sameKind(r.refKind, k.refKind);
}

/** Merge `patch` into the entry `k` names. Same state when none matches. */
export function patchReservation(state: TripState, k: ReservationKey, patch: Partial<Reservation>): TripState {
  const list = (state.reservations ?? []) as Reservation[];
  let touched = false;
  const next = list.map((r) => {
    if (!r || !matches(r, k)) return r;
    touched = true;
    return mergeDefined(r, patch);
  });
  return touched ? { ...state, reservations: next } : state;
}

export interface TrackSeed {
  type: string;
  refId: string;
  name: string;
  platform?: string | null;
  platformUrl?: string | null;
  opensDaysAhead?: number | null;
  opensAt?: string | null;
  difficulty?: string | null;
}

/**
 * Track a catalog place's booking window for `targetDate`. An entry for the
 * same place already on the trip gets the date (and comes back from
 * dismissed); otherwise a new 'tracking' entry with the catalog's window
 * snapshot, the same fields MCP add_reservation writes.
 */
export function trackReservation(state: TripState, seed: TrackSeed, targetDate: string): TripState {
  const refKind = refKindOfType(seed.type);
  const list = (state.reservations ?? []) as Reservation[];
  const existing = list.find((r) => r && r.refId === seed.refId && sameKind(r.refKind, refKind));
  if (existing) {
    const status = existing.status === 'dismissed' || existing.status === 'cancelled' ? 'tracking' : undefined;
    return patchReservation(state, keyOf(existing), { targetDate, ...(status ? { status } : {}) });
  }
  const record: Reservation = {
    id: genId(),
    status: 'tracking',
    createdAt: new Date().toISOString(),
    refId: seed.refId,
    refKind,
    name: seed.name,
    targetDate,
    opensOn: null,
    platform: seed.platform ?? null,
    platformUrl: seed.platformUrl ?? null,
    opensDaysAhead: typeof seed.opensDaysAhead === 'number' ? seed.opensDaysAhead : null,
    opensAt: seed.opensAt ?? null,
    difficulty: seed.difficulty ?? null,
  };
  return { ...state, reservations: appendReservation(list, record) };
}

/** Is this place already tracked (and not dismissed) on the trip? */
export function trackedEntry(state: TripState | null, type: string, refId: string): Reservation | null {
  const refKind = refKindOfType(type);
  const list = (state?.reservations ?? []) as Reservation[];
  return (
    list.find(
      (r) =>
        r &&
        r.refId === refId &&
        sameKind(r.refKind, refKind) &&
        r.status !== 'dismissed' &&
        r.status !== 'cancelled' &&
        r.status !== 'canceled',
    ) ?? null
  );
}
